// Mock data for the TalentForge prototype. No backend — every screen reads from here.
// India-context recruitment agency demo data.

export const currentTenant = { name: 'Inumellas Consultancy Services' }
export const currentUser = {
  name: 'Venkat Inumella',
  email: 'venkat@inumellas.com',
}

export type Stage =
  | 'sourced'
  | 'screened'
  | 'l1'
  | 'l2'
  | 'offer'
  | 'joined'

export const STAGES: { key: Stage; label: string }[] = [
  { key: 'sourced', label: 'Sourced' },
  { key: 'screened', label: 'Screened' },
  { key: 'l1', label: 'Interview L1' },
  { key: 'l2', label: 'Interview L2' },
  { key: 'offer', label: 'Offer' },
  { key: 'joined', label: 'Joined' },
]

export type JobStatus = 'open' | 'on_hold' | 'filled' | 'draft'

export interface Job {
  id: string
  title: string
  client: string
  location: string
  ctcRange: string
  feePct: number
  status: JobStatus
  candidates: number
  postedAt: string
  openings: number
}

export interface Candidate {
  id: string
  name: string
  title: string
  location: string
  totalYears: number
  currentCompany: string
  skills: string[]
  matchScore: number
  source: 'Naukri' | 'LinkedIn' | 'Internal DB' | 'Referral'
  stage: Stage
  jobId: string
  appliedFor: string
}

export interface ApprovalTask {
  id: string
  agent: 'Screening' | 'Sourcing' | 'Job Posting' | 'Interviews' | 'Bulk Email'
  step: string
  title: string
  summary: string
  jobTitle: string
  client: string
  count?: number
  /** AI's confidence in its proposal (0–100). High = safe to approve fast. */
  confidence: number
  createdAt: string
}

// ── Live agent status (for the dashboard "what's running now" strip) ──
export interface AgentStatus {
  name: 'Sourcing' | 'Screening' | 'Job Posting' | 'Interviews' | 'Bulk Email'
  state: 'running' | 'idle' | 'attention'
  detail: string
}
export const agentStatuses: AgentStatus[] = [
  { name: 'Sourcing', state: 'running', detail: 'Pulling profiles · DevOps Lead' },
  { name: 'Screening', state: 'running', detail: 'Ranking 31 · Data Scientist' },
  { name: 'Job Posting', state: 'idle', detail: 'Last run 4h ago' },
  { name: 'Interviews', state: 'idle', detail: 'Next: L2 today, 3:00 PM' },
  { name: 'Bulk Email', state: 'attention', detail: 'Campaign awaiting your approval' },
]

// URL-safe slug for each agent ('Job Posting' -> 'job-posting').
export function agentSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}
export function agentBySlug(slug: string): AgentStatus | undefined {
  return agentStatuses.find((a) => agentSlug(a.name) === slug)
}

// What each agent has been doing — the activity trail shown on its detail page.
export interface AgentEvent {
  id: string
  action: string
  detail: string
  time: string
  kind: 'running' | 'done'
}
export const agentActivity: Record<string, AgentEvent[]> = {
  Sourcing: [
    { id: 'sa1', action: 'Pulling profiles from Naukri', detail: 'DevOps Lead · 23 found so far', time: 'just now', kind: 'running' },
    { id: 'sa2', action: 'Pulled 28 profiles from Naukri', detail: 'DevOps Lead · queued for your review', time: '38 min ago', kind: 'done' },
    { id: 'sa3', action: 'Searched internal database', detail: 'Senior Backend Engineer · 64 matches', time: '2 hrs ago', kind: 'done' },
    { id: 'sa4', action: 'Pulled 19 profiles from LinkedIn', detail: 'Data Scientist', time: '5 hrs ago', kind: 'done' },
  ],
  Screening: [
    { id: 'sc1', action: 'Ranking candidates', detail: 'Data Scientist · scoring 31 profiles', time: 'just now', kind: 'running' },
    { id: 'sc2', action: 'Ranked 42 candidates', detail: 'Senior Backend Engineer · top 6 shortlisted', time: '12 min ago', kind: 'done' },
    { id: 'sc3', action: 'Parsed 28 new resumes', detail: 'DevOps Lead', time: '40 min ago', kind: 'done' },
    { id: 'sc4', action: 'Merged 3 duplicate profiles', detail: 'cleaned automatically', time: '1 hr ago', kind: 'done' },
  ],
  'Job Posting': [
    { id: 'jp1', action: 'Drafted AI-boosted JD', detail: 'Data Scientist · awaiting your review', time: '1 hr ago', kind: 'done' },
    { id: 'jp2', action: 'Published JD', detail: 'Engineering Manager · Naukri + LinkedIn', time: '4 hrs ago', kind: 'done' },
    { id: 'jp3', action: 'Re-promoted posting', detail: 'Senior Backend Engineer', time: 'Yesterday', kind: 'done' },
  ],
  Interviews: [
    { id: 'iv1', action: 'Scheduled L2', detail: 'Ananya Sharma · Acme panel, today 3:00 PM', time: '1 hr ago', kind: 'done' },
    { id: 'iv2', action: 'Sent calendar invites', detail: '2 interviewers · DevOps Lead', time: '3 hrs ago', kind: 'done' },
    { id: 'iv3', action: 'Collected L1 scorecard', detail: 'Vikram Singh · 4.2 / 5', time: '3 hrs ago', kind: 'done' },
  ],
  'Bulk Email': [
    { id: 'be1', action: 'Drafted 40 personalized emails', detail: 'Engineering Manager · awaiting your approval', time: '2 hrs ago', kind: 'done' },
    { id: 'be2', action: 'Sent 64 outreach emails', detail: 'Data Scientist · 22% reply rate', time: 'Yesterday', kind: 'done' },
    { id: 'be3', action: 'Paused campaign', detail: 'Frontend Engineer · role filled', time: '2 days ago', kind: 'done' },
  ],
}

export const dashboardStats = {
  openRoles: 6,
  candidatesInPipeline: 168,
  pendingApprovals: 6,
  // Recent placements (last ~90 days). The placements list is the source of
  // truth — pages derive the count from `placements.length` so they never disagree.
  placementsRecent: 5,
  hoursSavedThisWeek: 31,
  avgTimeToShortlist: '24 min',
}

export const jobs: Job[] = [
  { id: 'j1', title: 'Senior Backend Engineer', client: 'Acme Corp', location: 'Bengaluru', ctcRange: '₹28–38 LPA', feePct: 8.33, status: 'open', candidates: 42, postedAt: '2 days ago', openings: 2 },
  { id: 'j2', title: 'Engineering Manager', client: 'Nimbus Fintech', location: 'Mumbai', ctcRange: '₹45–60 LPA', feePct: 12.5, status: 'open', candidates: 18, postedAt: '4 days ago', openings: 1 },
  { id: 'j3', title: 'Data Scientist', client: 'Acme Corp', location: 'Remote', ctcRange: '₹22–30 LPA', feePct: 8.33, status: 'open', candidates: 31, postedAt: '1 week ago', openings: 3 },
  { id: 'j4', title: 'Product Designer', client: 'Lumen Health', location: 'Pune', ctcRange: '₹18–26 LPA', feePct: 8.33, status: 'on_hold', candidates: 12, postedAt: '1 week ago', openings: 1 },
  { id: 'j5', title: 'DevOps Lead', client: 'Nimbus Fintech', location: 'Hyderabad', ctcRange: '₹30–42 LPA', feePct: 10, status: 'open', candidates: 27, postedAt: '3 days ago', openings: 1 },
  { id: 'j6', title: 'Frontend Engineer', client: 'Quill Media', location: 'Bengaluru', ctcRange: '₹16–24 LPA', feePct: 8.33, status: 'filled', candidates: 38, postedAt: '3 weeks ago', openings: 2 },
  { id: 'j7', title: 'Senior Product Manager', client: 'Acme Corp', location: 'Bengaluru', ctcRange: '₹40–55 LPA', feePct: 8.33, status: 'open', candidates: 0, postedAt: 'just now', openings: 1 },
]

export const candidates: Candidate[] = [
  { id: 'c1', name: 'Ananya Sharma', title: 'Senior Backend Engineer', location: 'Bengaluru', totalYears: 7, currentCompany: 'Flipkart', skills: ['Go', 'PostgreSQL', 'Kubernetes', 'gRPC'], matchScore: 94, source: 'Naukri', stage: 'l2', jobId: 'j1', appliedFor: 'Senior Backend Engineer' },
  { id: 'c2', name: 'Rahul Verma', title: 'Backend Engineer', location: 'Bengaluru', totalYears: 6, currentCompany: 'Razorpay', skills: ['Node.js', 'PostgreSQL', 'AWS', 'Redis'], matchScore: 89, source: 'LinkedIn', stage: 'l1', jobId: 'j1', appliedFor: 'Senior Backend Engineer' },
  { id: 'c3', name: 'Priya Nair', title: 'Staff Engineer', location: 'Remote', totalYears: 9, currentCompany: 'Swiggy', skills: ['Go', 'Microservices', 'Kafka', 'GCP'], matchScore: 86, source: 'Internal DB', stage: 'screened', jobId: 'j1', appliedFor: 'Senior Backend Engineer' },
  { id: 'c4', name: 'Karthik Iyer', title: 'Senior SDE', location: 'Hyderabad', totalYears: 8, currentCompany: 'PhonePe', skills: ['Java', 'Spring', 'PostgreSQL', 'Kafka'], matchScore: 82, source: 'Naukri', stage: 'sourced', jobId: 'j1', appliedFor: 'Senior Backend Engineer' },
  { id: 'c5', name: 'Sneha Reddy', title: 'Engineering Manager', location: 'Mumbai', totalYears: 11, currentCompany: 'CRED', skills: ['Leadership', 'System Design', 'Hiring', 'Go'], matchScore: 91, source: 'LinkedIn', stage: 'offer', jobId: 'j2', appliedFor: 'Engineering Manager' },
  { id: 'c6', name: 'Arjun Desai', title: 'Data Scientist', location: 'Remote', totalYears: 5, currentCompany: 'Myntra', skills: ['Python', 'PyTorch', 'SQL', 'LLMs'], matchScore: 88, source: 'Naukri', stage: 'l1', jobId: 'j3', appliedFor: 'Data Scientist' },
  { id: 'c7', name: 'Meera Krishnan', title: 'ML Engineer', location: 'Bengaluru', totalYears: 6, currentCompany: 'Zomato', skills: ['Python', 'TensorFlow', 'Spark', 'AWS'], matchScore: 84, source: 'Internal DB', stage: 'screened', jobId: 'j3', appliedFor: 'Data Scientist' },
  { id: 'c8', name: 'Vikram Singh', title: 'DevOps Lead', location: 'Hyderabad', totalYears: 10, currentCompany: 'Freshworks', skills: ['Terraform', 'AWS', 'Kubernetes', 'CI/CD'], matchScore: 90, source: 'LinkedIn', stage: 'l2', jobId: 'j5', appliedFor: 'DevOps Lead' },
  { id: 'c9', name: 'Divya Patel', title: 'Senior Backend Engineer', location: 'Pune', totalYears: 7, currentCompany: 'Postman', skills: ['Go', 'PostgreSQL', 'Docker', 'REST'], matchScore: 80, source: 'Naukri', stage: 'sourced', jobId: 'j1', appliedFor: 'Senior Backend Engineer' },
  { id: 'c10', name: 'Aditya Rao', title: 'Backend Engineer', location: 'Bengaluru', totalYears: 5, currentCompany: 'Meesho', skills: ['Node.js', 'MongoDB', 'GCP'], matchScore: 76, source: 'Referral', stage: 'sourced', jobId: 'j1', appliedFor: 'Senior Backend Engineer' },
  { id: 'c11', name: 'Nisha Gupta', title: 'Data Scientist', location: 'Remote', totalYears: 4, currentCompany: 'Ola', skills: ['Python', 'scikit-learn', 'SQL'], matchScore: 79, source: 'Naukri', stage: 'sourced', jobId: 'j3', appliedFor: 'Data Scientist' },
  { id: 'c12', name: 'Rohan Mehta', title: 'Staff DevOps', location: 'Remote', totalYears: 12, currentCompany: 'Atlassian', skills: ['Kubernetes', 'AWS', 'Go', 'Observability'], matchScore: 87, source: 'LinkedIn', stage: 'screened', jobId: 'j5', appliedFor: 'DevOps Lead' },
  { id: 'c13', name: 'Sanjana Pillai', title: 'Senior Frontend Engineer', location: 'Bengaluru', totalYears: 6, currentCompany: 'Razorpay', skills: ['React', 'TypeScript', 'Next.js'], matchScore: 83, source: 'Naukri', stage: 'sourced', jobId: 'j6', appliedFor: 'Frontend Engineer' },
  { id: 'c14', name: 'Imran Khan', title: 'Backend Engineer', location: 'Pune', totalYears: 5, currentCompany: 'BrowserStack', skills: ['Go', 'Redis', 'PostgreSQL'], matchScore: 78, source: 'Internal DB', stage: 'sourced', jobId: 'j1', appliedFor: 'Senior Backend Engineer' },
  { id: 'c15', name: 'Tanvi Joshi', title: 'Product Designer', location: 'Pune', totalYears: 5, currentCompany: 'Zeta', skills: ['Figma', 'Design Systems', 'UX Research'], matchScore: 81, source: 'LinkedIn', stage: 'screened', jobId: 'j4', appliedFor: 'Product Designer' },
  { id: 'c16', name: 'Deepak Kumar', title: 'Data Scientist', location: 'Bengaluru', totalYears: 7, currentCompany: 'Walmart Labs', skills: ['Python', 'PyTorch', 'MLOps', 'SQL'], matchScore: 85, source: 'Naukri', stage: 'screened', jobId: 'j3', appliedFor: 'Data Scientist' },
  { id: 'c17', name: 'Fatima Sheikh', title: 'Engineering Manager', location: 'Mumbai', totalYears: 10, currentCompany: 'Groww', skills: ['Leadership', 'System Design', 'Hiring'], matchScore: 83, source: 'LinkedIn', stage: 'l1', jobId: 'j2', appliedFor: 'Engineering Manager' },
  { id: 'c18', name: 'Aarav Bose', title: 'DevOps Engineer', location: 'Hyderabad', totalYears: 6, currentCompany: 'Darwinbox', skills: ['AWS', 'Terraform', 'Docker'], matchScore: 77, source: 'Naukri', stage: 'sourced', jobId: 'j5', appliedFor: 'DevOps Lead' },
  { id: 'c19', name: 'Ishita Roy', title: 'Frontend Engineer', location: 'Remote', totalYears: 4, currentCompany: 'Hasura', skills: ['React', 'GraphQL', 'TypeScript'], matchScore: 74, source: 'Referral', stage: 'sourced', jobId: 'j6', appliedFor: 'Frontend Engineer' },
  { id: 'c20', name: 'Manish Agarwal', title: 'Senior Data Scientist', location: 'Remote', totalYears: 9, currentCompany: 'Gojek', skills: ['Python', 'LLMs', 'Spark', 'SQL'], matchScore: 92, source: 'LinkedIn', stage: 'l2', jobId: 'j3', appliedFor: 'Data Scientist' },
  { id: 'c21', name: 'Pooja Shetty', title: 'Frontend Engineer', location: 'Bengaluru', totalYears: 6, currentCompany: 'Quill Media', skills: ['React', 'TypeScript', 'Next.js'], matchScore: 88, source: 'LinkedIn', stage: 'joined', jobId: 'j6', appliedFor: 'Frontend Engineer' },
  { id: 'c22', name: 'Karan Malhotra', title: 'DevOps Engineer', location: 'Hyderabad', totalYears: 8, currentCompany: 'Nimbus Fintech', skills: ['AWS', 'Kubernetes', 'Terraform'], matchScore: 90, source: 'Naukri', stage: 'joined', jobId: 'j5', appliedFor: 'DevOps Lead' },
]

// Candidate profile detail (for the candidate profile screen) — keyed by id.
export interface CandidateDetail extends Candidate {
  email: string
  phone: string
  noticePeriod: string
  currentCtc: string
  expectedCtc: string
  experience: { title: string; company: string; period: string; summary: string }[]
  education: { degree: string; institution: string; year: string }[]
  matchRationale: {
    score: number
    matched: { skill: string; evidence: string }[]
    gaps: string[]
    summary: string
  }
}

export const candidateDetails: Record<string, CandidateDetail> = {
  c1: {
    ...candidates[0],
    email: 'ananya.sharma@email.com',
    phone: '+91 98xxxx2310',
    noticePeriod: '30 days',
    currentCtc: '₹26 LPA',
    expectedCtc: '₹34 LPA',
    experience: [
      { title: 'Senior Backend Engineer', company: 'Flipkart', period: '2021 – Present', summary: 'Led the payments platform team; built a Go + gRPC service handling 40k req/s on Kubernetes.' },
      { title: 'Backend Engineer', company: 'Cleartrip', period: '2018 – 2021', summary: 'Owned the booking service; migrated a monolith to microservices on PostgreSQL.' },
      { title: 'Software Engineer', company: 'Infosys', period: '2017 – 2018', summary: 'Built internal tooling in Java for enterprise clients.' },
    ],
    education: [
      { degree: 'B.Tech, Computer Science', institution: 'NIT Trichy', year: '2017' },
    ],
    matchRationale: {
      score: 94,
      matched: [
        { skill: 'Go', evidence: 'Built Go + gRPC payments service at Flipkart (4 yrs)' },
        { skill: 'PostgreSQL', evidence: 'Migrated booking monolith to PostgreSQL microservices' },
        { skill: 'Kubernetes', evidence: 'Runs 40k req/s service on Kubernetes' },
        { skill: 'gRPC', evidence: 'Core of the payments platform she led' },
      ],
      gaps: ['No explicit Kafka experience (JD lists it as a plus)'],
      summary:
        'Strong match: 7 years backend, deep Go + PostgreSQL + Kubernetes exactly matching the JD core. Currently at a top product company at a comparable CTC band, 30-day notice. Only minor gap is Kafka, listed as nice-to-have.',
    },
  },
}

export const recentActivity = [
  { id: 'a1', agent: 'Screening', text: 'Ranked 42 candidates for Senior Backend Engineer · top 6 shortlisted', time: '12 min ago' },
  { id: 'a2', agent: 'Sourcing', text: 'Pulled 28 new profiles from Naukri for DevOps Lead', time: '38 min ago' },
  { id: 'a3', agent: 'Interviews', text: 'Scheduled L2 for Ananya Sharma with Acme panel', time: '1 hr ago' },
  { id: 'a4', agent: 'Bulk Email', text: 'Sent 64 personalized outreach emails for Data Scientist', time: '2 hrs ago' },
  { id: 'a5', agent: 'Job Posting', text: 'Published Engineering Manager to Naukri + LinkedIn', time: '4 hrs ago' },
]

export const approvalTasks: ApprovalTask[] = [
  { id: 't1', agent: 'Screening', step: 'shortlist', title: 'Shortlist ready — Senior Backend Engineer', summary: 'Screened 42 candidates, ranked the top 6 for Acme Corp. Review before sending to client.', jobTitle: 'Senior Backend Engineer', client: 'Acme Corp', count: 6, confidence: 96, createdAt: '12 min ago' },
  { id: 't2', agent: 'Sourcing', step: 'board_search', title: 'Sourcing batch — DevOps Lead', summary: '28 new profiles pulled from Naukri for Nimbus Fintech. Approve to add to pipeline.', jobTitle: 'DevOps Lead', client: 'Nimbus Fintech', count: 28, confidence: 88, createdAt: '38 min ago' },
  { id: 't3', agent: 'Job Posting', step: 'publish', title: 'JD ready to publish — Data Scientist', summary: 'AI-boosted JD generated for Acme Corp. Review before publishing to boards.', jobTitle: 'Data Scientist', client: 'Acme Corp', confidence: 91, createdAt: '1 hr ago' },
  { id: 't4', agent: 'Bulk Email', step: 'campaign', title: 'Outreach campaign — Engineering Manager', summary: '40 personalized emails drafted for passive candidates. Approve to send via Brevo.', jobTitle: 'Engineering Manager', client: 'Nimbus Fintech', count: 40, confidence: 79, createdAt: '2 hrs ago' },
  { id: 't5', agent: 'Interviews', step: 'round_advance', title: 'Advance to L2 — Vikram Singh', summary: 'L1 scorecard complete (4.2/5). Recommend advancing to L2 for DevOps Lead.', jobTitle: 'DevOps Lead', client: 'Nimbus Fintech', confidence: 90, createdAt: '3 hrs ago' },
  { id: 't6', agent: 'Screening', step: 'shortlist', title: 'Shortlist ready — Data Scientist', summary: 'Ranked 31 candidates for Acme Corp, top 5 shortlisted with explainable scores.', jobTitle: 'Data Scientist', client: 'Acme Corp', count: 5, confidence: 93, createdAt: '5 hrs ago' },
]

export function candidatesByStage(stage: Stage): Candidate[] {
  return candidates.filter((c) => c.stage === stage)
}

export interface Client {
  id: string
  name: string
  industry: string
  location: string
  openRoles: number
  activeCandidates: number
  feePct: number
  guaranteeDays: number
  contactName: string
  contactEmail: string
  since: string
}

export const clients: Client[] = [
  { id: 'cl1', name: 'Acme Corp', industry: 'E-commerce', location: 'Bengaluru', openRoles: 3, activeCandidates: 73, feePct: 8.33, guaranteeDays: 90, contactName: 'Neha Kapoor', contactEmail: 'neha@acme.com', since: 'Jan 2024' },
  { id: 'cl2', name: 'Nimbus Fintech', industry: 'Fintech', location: 'Mumbai', openRoles: 2, activeCandidates: 45, feePct: 12.5, guaranteeDays: 90, contactName: 'Sameer Bhatia', contactEmail: 'sameer@nimbus.in', since: 'Mar 2024' },
  { id: 'cl3', name: 'Lumen Health', industry: 'Healthtech', location: 'Pune', openRoles: 1, activeCandidates: 12, feePct: 8.33, guaranteeDays: 60, contactName: 'Dr. Asha Rao', contactEmail: 'asha@lumen.health', since: 'Jul 2024' },
  { id: 'cl4', name: 'Quill Media', industry: 'Media', location: 'Bengaluru', openRoles: 0, activeCandidates: 38, feePct: 8.33, guaranteeDays: 60, contactName: 'Vivek Anand', contactEmail: 'vivek@quill.media', since: 'Nov 2023' },
]

export interface Interview {
  id: string
  candidateName: string
  candidateId: string
  jobTitle: string
  client: string
  round: string
  scheduledAt: string
  day: string
  panel: string
  mode: 'Video' | 'In-person' | 'Phone'
  status: 'scheduled' | 'completed' | 'awaiting_feedback'
}

export const interviews: Interview[] = [
  { id: 'i1', candidateName: 'Ananya Sharma', candidateId: 'c1', jobTitle: 'Senior Backend Engineer', client: 'Acme Corp', round: 'L2', scheduledAt: 'Today, 3:00 PM', day: 'today', panel: 'Neha Kapoor + 1', mode: 'Video', status: 'scheduled' },
  { id: 'i2', candidateName: 'Rahul Verma', candidateId: 'c2', jobTitle: 'Senior Backend Engineer', client: 'Acme Corp', round: 'L1', scheduledAt: 'Today, 5:30 PM', day: 'today', panel: 'Tech screen (auto)', mode: 'Video', status: 'scheduled' },
  { id: 'i3', candidateName: 'Vikram Singh', candidateId: 'c8', jobTitle: 'DevOps Lead', client: 'Nimbus Fintech', round: 'L1', scheduledAt: 'Yesterday, 11:00 AM', day: 'past', panel: 'Sameer Bhatia', mode: 'Video', status: 'awaiting_feedback' },
  { id: 'i4', candidateName: 'Arjun Desai', candidateId: 'c6', jobTitle: 'Data Scientist', client: 'Acme Corp', round: 'L1', scheduledAt: 'Tomorrow, 12:00 PM', day: 'upcoming', panel: 'Neha Kapoor', mode: 'Video', status: 'scheduled' },
  { id: 'i5', candidateName: 'Manish Agarwal', candidateId: 'c20', jobTitle: 'Data Scientist', client: 'Acme Corp', round: 'L2', scheduledAt: 'Thu, 4:00 PM', day: 'upcoming', panel: 'Hiring manager', mode: 'Video', status: 'scheduled' },
]

export interface Placement {
  id: string
  candidateName: string
  jobTitle: string
  client: string
  joiningDate: string
  ctc: string
  feeAmount: string
  guaranteeEnds: string
  status: 'joined' | 'offered' | 'guarantee_active'
}

export const placements: Placement[] = [
  { id: 'p1', candidateName: 'Sneha Reddy', jobTitle: 'Engineering Manager', client: 'Nimbus Fintech', joiningDate: '01 Jun 2026', ctc: '₹52 LPA', feeAmount: '₹6.50 L', guaranteeEnds: '30 Aug 2026', status: 'guarantee_active' },
  { id: 'p2', candidateName: 'Karan Malhotra', jobTitle: 'DevOps Engineer', client: 'Nimbus Fintech', joiningDate: '28 May 2026', ctc: '₹30 LPA', feeAmount: '₹3.00 L', guaranteeEnds: '26 Aug 2026', status: 'guarantee_active' },
  { id: 'p3', candidateName: 'Pooja Shetty', jobTitle: 'Frontend Engineer', client: 'Quill Media', joiningDate: '15 May 2026', ctc: '₹22 LPA', feeAmount: '₹1.83 L', guaranteeEnds: '13 Jul 2026', status: 'guarantee_active' },
  { id: 'p4', candidateName: 'Rohit Nanda', jobTitle: 'Backend Engineer', client: 'Acme Corp', joiningDate: '10 Apr 2026', ctc: '₹28 LPA', feeAmount: '₹2.33 L', guaranteeEnds: 'Completed', status: 'joined' },
  { id: 'p5', candidateName: 'Anjali Menon', jobTitle: 'Product Designer', client: 'Lumen Health', joiningDate: '24 Mar 2026', ctc: '₹20 LPA', feeAmount: '₹1.60 L', guaranteeEnds: 'Completed', status: 'joined' },
]

export interface Invoice {
  id: string
  number: string
  client: string
  candidate: string
  amount: string
  gst: string
  total: string
  sac: string
  status: 'paid' | 'sent' | 'draft'
  dueDate: string
}

export const invoices: Invoice[] = [
  { id: 'inv1', number: 'TF-2026-031', client: 'Nimbus Fintech', candidate: 'Sneha Reddy', amount: '₹6,50,000', gst: '₹1,17,000', total: '₹7,67,000', sac: '9985', status: 'sent', dueDate: '15 Jul 2026' },
  { id: 'inv2', number: 'TF-2026-028', client: 'Quill Media', candidate: 'Pooja Shetty', amount: '₹1,83,000', gst: '₹32,940', total: '₹2,15,940', sac: '9985', status: 'paid', dueDate: '30 Jun 2026' },
  { id: 'inv3', number: 'TF-2026-033', client: 'Acme Corp', candidate: 'Ananya Sharma', amount: '₹2,83,000', gst: '₹50,940', total: '₹3,33,940', sac: '9985', status: 'draft', dueDate: 'On joining' },
]

export function candidatesByJob(jobId: string): Candidate[] {
  return candidates.filter((c) => c.jobId === jobId)
}

export function clientById(id: string): Client | undefined {
  return clients.find((c) => c.id === id)
}
export function jobById(id: string): Job | undefined {
  return jobs.find((j) => j.id === id)
}

// ── Settings: autonomy gates (auto | review | off per agent step) ──
export type GateMode = 'auto' | 'review' | 'off'
export interface GateRow {
  agent: string
  step: string
  mode: GateMode
  note: string
}
export const gateConfig: GateRow[] = [
  { agent: 'Job Posting', step: 'Publish to boards', mode: 'review', note: 'Goes out on your brand' },
  { agent: 'Sourcing', step: 'Internal DB search', mode: 'auto', note: 'Reversible, internal' },
  { agent: 'Sourcing', step: 'Board search (Naukri/LinkedIn)', mode: 'review', note: 'Uses your paid seat' },
  { agent: 'Screening', step: 'Rank candidates', mode: 'auto', note: 'Internal compute' },
  { agent: 'Screening', step: 'Shortlist to client', mode: 'review', note: 'PII leaves to a client' },
  { agent: 'Interviews', step: 'Schedule', mode: 'auto', note: 'Reversible' },
  { agent: 'Interviews', step: 'Advance round', mode: 'review', note: 'Human judgement' },
  { agent: 'Bulk Email', step: 'Send campaign', mode: 'review', note: 'Consent-sensitive' },
]

// ── Settings: team ──
export interface TeamMember {
  id: string
  name: string
  email: string
  role: 'Owner' | 'Admin' | 'Recruiter' | 'Viewer'
  status: 'active' | 'invited'
}
export const teamMembers: TeamMember[] = [
  { id: 'u1', name: 'Venkat Inumella', email: 'venkat@inumellas.com', role: 'Owner', status: 'active' },
  { id: 'u2', name: 'Priya Reddy', email: 'priya@inumellas.com', role: 'Recruiter', status: 'active' },
  { id: 'u3', name: 'Arun Kishore', email: 'arun@inumellas.com', role: 'Recruiter', status: 'active' },
  { id: 'u4', name: 'Sara Thomas', email: 'sara@inumellas.com', role: 'Recruiter', status: 'invited' },
  { id: 'u5', name: 'Mohan Das', email: 'mohan@inumellas.com', role: 'Viewer', status: 'active' },
]

// ── Settings: connected board seats ──
export interface ConnectedSeat {
  id: string
  platform: 'Naukri / Resdex' | 'LinkedIn Recruiter'
  account: string
  status: 'connected' | 'needs_login'
  lastSync: string
  dailyCap: number
}
export const connectedSeats: ConnectedSeat[] = [
  { id: 's1', platform: 'Naukri / Resdex', account: 'recruiter@inumellas.com', status: 'connected', lastSync: '8 min ago', dailyCap: 80 },
  { id: 's2', platform: 'LinkedIn Recruiter', account: 'venkat@inumellas.com', status: 'needs_login', lastSync: '2 days ago', dailyCap: 50 },
]

// ── Settings: plan & usage ──
export const planUsage = {
  plan: 'Growth',
  floor: '₹5,000 / month',
  successPct: 5,
  creditsIncluded: 5600,
  creditsUsed: 3120,
  runsIncluded: 60,
  runsUsed: 41,
  billThisMonth: '₹9,400',
  placementsBilled: 9,
}

// ── Vendors & commissions ──
export interface Vendor {
  id: string
  name: string
  gstin: string
  defaultSplitPct: number
  status: 'active'
}
export const vendors: Vendor[] = [
  { id: 'v1', name: 'TalentBridge Associates', gstin: '36ABCDE1234F1Z5', defaultSplitPct: 40, status: 'active' },
  { id: 'v2', name: 'HireWorks Partners', gstin: '29XYZAB5678C1Z2', defaultSplitPct: 35, status: 'active' },
]
export interface Commission {
  id: string
  candidate: string
  client: string
  vendor: string
  feeAmount: string
  splitPct: number
  payable: string
  status: 'pending' | 'approved' | 'paid'
}
export const commissions: Commission[] = [
  { id: 'cm1', candidate: 'Sneha Reddy', client: 'Nimbus Fintech', vendor: 'TalentBridge Associates', feeAmount: '₹6,50,000', splitPct: 40, payable: '₹2,60,000', status: 'approved' },
  { id: 'cm2', candidate: 'Pooja Shetty', client: 'Quill Media', vendor: 'HireWorks Partners', feeAmount: '₹1,83,000', splitPct: 35, payable: '₹64,050', status: 'paid' },
  { id: 'cm3', candidate: 'Ananya Sharma', client: 'Acme Corp', vendor: 'TalentBridge Associates', feeAmount: '₹2,83,000', splitPct: 40, payable: '₹1,13,200', status: 'pending' },
]

// ── Notifications ──
export interface Notification {
  id: string
  type: 'approval' | 'rpa' | 'interview' | 'placement' | 'system'
  title: string
  body: string
  time: string
  read: boolean
}
export const notifications: Notification[] = [
  { id: 'n1', type: 'approval', title: 'Shortlist ready for review', body: 'Senior Backend Engineer — 6 candidates for Acme Corp', time: '12 min ago', read: false },
  { id: 'n3', type: 'interview', title: 'Scorecard pending', body: 'Vikram Singh — L1 for DevOps Lead, due today', time: '1 hr ago', read: false },
  { id: 'n4', type: 'placement', title: 'Candidate joined', body: 'Sneha Reddy joined Nimbus Fintech — guarantee clock started', time: '3 hrs ago', read: true },
  { id: 'n5', type: 'system', title: 'LinkedIn seat needs re-login', body: 'Connect your seat again to keep sourcing', time: 'Yesterday', read: true },
]

// ── Audit log ──
export interface AuditEntry {
  id: string
  actor: string
  actorType: 'user' | 'agent' | 'system'
  action: string
  entity: string
  time: string
  /** Absolute timestamp — shown on hover; auditors need exact times. */
  at: string
}
export const auditLog: AuditEntry[] = [
  { id: 'al1', actor: 'Screening agent', actorType: 'agent', action: 'Ranked 42 candidates', entity: 'Senior Backend Engineer', time: '12 min ago', at: '20 Jun 2026, 2:48 PM' },
  { id: 'al2', actor: 'Venkat Inumella', actorType: 'user', action: 'Approved shortlist', entity: 'Senior Backend Engineer', time: '11 min ago', at: '20 Jun 2026, 2:49 PM' },
  { id: 'al3', actor: 'Sourcing agent', actorType: 'agent', action: 'Pulled 28 profiles from Naukri', entity: 'DevOps Lead', time: '38 min ago', at: '20 Jun 2026, 2:22 PM' },
  { id: 'al4', actor: 'Priya Reddy', actorType: 'user', action: 'Edited JD', entity: 'Data Scientist', time: '1 hr ago', at: '20 Jun 2026, 2:00 PM' },
  { id: 'al5', actor: 'System', actorType: 'system', action: 'Generated GST invoice', entity: 'TF-2026-031 · Nimbus Fintech', time: '3 hrs ago', at: '20 Jun 2026, 12:01 PM' },
  { id: 'al6', actor: 'Interviews agent', actorType: 'agent', action: 'Scheduled L2', entity: 'Ananya Sharma', time: '1 hr ago', at: '20 Jun 2026, 1:55 PM' },
]

// ── Consent / DPDP ──
export interface ConsentRecord {
  id: string
  candidate: string
  purpose: string
  basis: 'voluntary' | 'cold_source'
  status: 'granted' | 'withdrawn' | 'pending'
  date: string
}
export const consentRecords: ConsentRecord[] = [
  { id: 'co1', candidate: 'Ananya Sharma', purpose: 'Screening & client submission', basis: 'voluntary', status: 'granted', date: '18 Jun 2026' },
  { id: 'co2', candidate: 'Rahul Verma', purpose: 'Talent pool', basis: 'voluntary', status: 'granted', date: '17 Jun 2026' },
  { id: 'co3', candidate: 'Karthik Iyer', purpose: 'Screening', basis: 'cold_source', status: 'pending', date: '20 Jun 2026' },
  { id: 'co4', candidate: 'Priya Nair', purpose: 'Marketing', basis: 'voluntary', status: 'withdrawn', date: '15 Jun 2026' },
]
export interface DataRequest {
  id: string
  candidate: string
  type: 'Access' | 'Erasure' | 'Correction'
  status: 'open' | 'completed'
  due: string
}
export const dataRequests: DataRequest[] = [
  { id: 'dr1', candidate: 'Priya Nair', type: 'Erasure', status: 'open', due: 'in 5 days' },
  { id: 'dr2', candidate: 'Deepak Kumar', type: 'Access', status: 'completed', due: '—' },
]

// ── Analytics ──
export const analytics = {
  funnel: [
    { stage: 'Sourced', count: 213 },
    { stage: 'Screened', count: 118 },
    { stage: 'Interview L1', count: 64 },
    { stage: 'Interview L2', count: 31 },
    { stage: 'Offer', count: 14 },
    { stage: 'Joined', count: 9 },
  ],
  placementsTrend: [
    { month: 'Jan', placements: 4 },
    { month: 'Feb', placements: 6 },
    { month: 'Mar', placements: 5 },
    { month: 'Apr', placements: 7 },
    { month: 'May', placements: 8 },
    { month: 'Jun', placements: 9 },
  ],
  sourceMix: [
    { source: 'Naukri', count: 96 },
    { source: 'LinkedIn', count: 71 },
    { source: 'Internal DB', count: 34 },
    { source: 'Referral', count: 12 },
  ],
  timeToFillDays: 21,
  offerAcceptRate: 82,
}
